package com.example.positionahead.domain

import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.DiscoverIdea
import com.example.positionahead.model.DiscoverSectionKind
import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.Position
import com.example.positionahead.model.Quote
import com.example.positionahead.model.RebalanceTrade
import com.example.positionahead.model.TargetHolding
import com.example.positionahead.model.totalShares
import com.example.positionahead.model.effectiveBondTarget
import com.example.positionahead.model.effectiveStockTarget
import kotlin.math.abs
import kotlin.random.Random

/**
 * Builds the "suggested" portfolio: goal-aligned targets, rebalance trades,
 * and Discover picks to fill gaps when you don't yet have trades.
 */
object SuggestedPortfolioBuilder {

    data class BuildResult(
        val positions: List<Position>,
        val addedFromDiscover: List<DiscoverIdea>,
        val description: String,
    )

    fun build(
        currentPositions: List<Position>,
        summary: PortfolioSummary,
        goal: FinancialGoal,
        targetHoldings: List<TargetHolding>,
        trades: List<RebalanceTrade>,
        discoverSections: List<com.example.positionahead.model.DiscoverSection>,
        quotes: Map<String, Quote>,
    ): BuildResult {
        if (trades.isNotEmpty()) {
            val positions = PlanSimulator.applyTrades(currentPositions, trades, quotes)
            return BuildResult(
                positions = positions,
                addedFromDiscover = emptyList(),
                description = "Suggested portfolio after applying dollar rebalance trades toward your goals.",
            )
        }

        val total = summary.totalMarketValue
        if (total <= 0) {
            return BuildResult(emptyList(), emptyList(), "Add holdings to generate a suggestion.")
        }

        val targetPositions = GoalTargetPortfolioBuilder.positionsFromTargets(targetHoldings, quotes).toMutableList()
        val targetValue = targetPositions.sumOf { pos ->
            val price = quotes[pos.symbol.uppercase()]?.price ?: 0.0
            price * pos.totalShares()
        }
        var gapDollars = (total - targetValue).coerceAtLeast(0.0)

        val goalIdeas = discoverSections
            .find { it.kind == DiscoverSectionKind.FOR_YOUR_GOAL }
            ?.ideas
            .orEmpty()

        val stockTarget = goal.effectiveStockTarget().toDouble()
        val bondTarget = goal.effectiveBondTarget().toDouble()
        val needsBonds = summary.stockAllocationPercent > stockTarget + goal.rebalanceThresholdPercent
        val needsStocks = summary.stockAllocationPercent < stockTarget - goal.rebalanceThresholdPercent

        val pickIdeas = when {
            needsBonds -> goalIdeas.filter { it.assetClass == com.example.positionahead.model.DiscoverAssetClass.BOND }
                .ifEmpty { goalIdeas }
            needsStocks -> goalIdeas.filter {
                it.assetClass == com.example.positionahead.model.DiscoverAssetClass.ETF ||
                    it.assetClass == com.example.positionahead.model.DiscoverAssetClass.STOCK
            }.ifEmpty { goalIdeas }
            else -> goalIdeas
        }.take(3)

        val added = mutableListOf<DiscoverIdea>()
        if (gapDollars > 50 && pickIdeas.isNotEmpty()) {
            val perIdea = gapDollars / pickIdeas.size
            pickIdeas.forEach { idea ->
                val symbol = idea.symbol.uppercase()
                if (targetPositions.any { it.symbol.equals(symbol, ignoreCase = true) }) return@forEach
                val price = quotes[symbol]?.price?.takeIf { it > 0 } ?: idea.price.takeIf { it > 0 } ?: return@forEach
                val shares = perIdea / price
                if (shares <= 0.0001) return@forEach
                targetPositions += positionFor(symbol, shares, price, "Discover · ${idea.whyConsider.take(40)}")
                added += idea
            }
            gapDollars = 0.0
        }

        val desc = buildString {
            append("Goal mix ${stockTarget.toInt()}% stocks / ${bondTarget.toInt()}% bonds, ")
            append("max ${goal.maxSinglePositionPercent.toInt()}% per holding.")
            if (added.isNotEmpty()) {
                append(" Includes ${added.joinToString { it.symbol }} from Discover for your goal.")
            } else if (abs(summary.stockAllocationPercent - stockTarget) <= goal.rebalanceThresholdPercent) {
                append(" Your current symbols reweighted toward targets.")
            }
        }

        return BuildResult(
            positions = targetPositions,
            addedFromDiscover = added,
            description = desc,
        )
    }

    private fun positionFor(symbol: String, shares: Double, price: Double, note: String): Position =
        Position(
            id = Random.nextLong().toString(16),
            symbol = symbol.uppercase(),
            assetClass = AssetClass.ETF,
            lots = listOf(
                com.example.positionahead.model.PositionLot(
                    id = Random.nextLong().toString(16),
                    shares = shares,
                    costBasisPerShare = price,
                    purchaseDate = "Suggested",
                    note = note,
                ),
            ),
        )
}
